import { objectValues } from '@hsp/utilities';

import { IResult } from '@t/api';
import { BluebookEnumTabs } from '@t/api/bluebook/bluebook.enum';
import { BluebookParameters } from '@t/api/bluebook/bluebook-dashboard-parameters.model';
import { IClientReturns } from '@t/api/bluebook/client-return/client-return.model';
import { ReturnReportParameters } from '@t/api/bluebook/client-return/client-return-parameters.models';
import { IListPerformance } from '@t/api/bluebook/client-return/list-performance.model';
import { IIncidentalList } from '@t/api/bluebook/incidental/incidental-list.model';
import { IReturnListRental } from '@t/api/bluebook/list-rental/lri-lists.model';
import { IWhiteMailList } from '@t/api/bluebook/whitemail/whitemail-list.model';
import { IBluebookClient } from '@t/api/client-list/bluebook-client.model';

import { Uri } from 'constants/uri';
import { BaseHttpService } from 'services/http';

class ReturnServices extends BaseHttpService {
  constructor() {
    super({ endpoints: objectValues(Uri.Returns) as string[] });
  }

  getIncidentals(clientAcronym: string, cancel = false): Promise<IResult<IIncidentalList[]>> {
    const url = Uri.Returns.Incidentals;

    return this.get({
      ...this.defaultRequestConfig(url, cancel),
      params: { clientAcronym },
    });
  }

  getLRI(clientAcronym: string, cancel = false): Promise<IResult<IReturnListRental[]>> {
    const url = Uri.Returns.LRI;

    return this.get({
      ...this.defaultRequestConfig(url, cancel),
      params: { clientAcronym },
    });
  }

  getMailTypes(clientAcronym: string, cancel = false): Promise<IResult<string[]>> {
    const url = Uri.Returns.MailTypes;

    return this.get({
      ...this.defaultRequestConfig(url, cancel),
      params: { clientAcronym },
    });
  }

  getPackageTotals(packageName: string, cancel = false): Promise<IResult<IListPerformance>> | undefined {
    const url = Uri.Returns.Package;

    return this.get({
      ...this.defaultRequestConfig(url, cancel),
      params: { packageName },
    });
  }

  getReturnClients(parameters?: BluebookParameters, cancel = false): Promise<IResult<IBluebookClient[]>> {
    const url = Uri.Returns.Clients;

    return this.get({
      ...this.defaultRequestConfig(url, cancel),
      params: {
        endDate: parameters?.endDate,
        filter: parameters?.filter,
        isInactive: parameters?.isInactive,
        startDate: parameters?.startDate,
      },
    });
  }

  getReturns(parameters: ReturnReportParameters, cancel = false): Promise<IResult<IClientReturns[]>> {
    const url = Uri.Returns.Return;
    const { clientAcronym, endDate, mailType, startDate, status } = parameters;
    const mailTypeParam = mailType !== BluebookEnumTabs.All ? mailType : '';

    return this.get({
      ...this.defaultRequestConfig(url, cancel),
      params: {
        clientAcronym,
        endDate,
        mailType: mailTypeParam,
        startDate,
        status,
      },
    });
  }

  getWhiteMail(clientAcronym: string, cancel = false): Promise<IResult<IWhiteMailList[]>> {
    const url = Uri.Returns.WhiteMail;

    return this.get({
      ...this.defaultRequestConfig(url, cancel),
      params: { clientAcronym },
    });
  }
}

export const returnServices = new ReturnServices();
